/*
 * Purpose: Browse AutoBlocks connector resource library (built-in + AI-built)
 * Last modified: 2026-06-25 | Agent
 * Completeness: 85
 */
import { useConnectorLibraryStore } from '@/services/connectors/library/store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Library, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { PageMotion } from '@/components/motion/PageMotion';
import { motionClasses } from '@/motion';

export function ConnectorLibraryBrowser() {
  const { customConnectors, load, getAll, remove } = useConnectorLibraryStore();

  useEffect(() => {
    load();
  }, [load]);

  const all = getAll();

  return (
    <PageMotion className="space-y-4 max-w-3xl">
      <div className="flex items-center gap-2">
        <Library className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">{PRODUCT_TERMS.autoBlocksFactory}</h2>
        <Badge variant="outline">{all.length} integrations</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        The {PRODUCT_TERMS.autoBlocksFactory} contains the {PRODUCT_TERMS.blockGenerator} — reusable
        integrations and primitives that become {PRODUCT_TERMS.blocks}. AI may add custom entries
        when planning outcomes; they persist in your browser.
      </p>
      <div className={`grid gap-3 sm:grid-cols-2 ${motionClasses.staggerGrid}`}>
        {all.map((c) => (
          <Card key={c.id} className={`text-sm ${motionClasses.objectInteractive}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between gap-2">
                <CardTitle className="text-base">{c.name}</CardTitle>
                <Badge variant={c.vendor === 'ai_generated' ? 'secondary' : 'default'} className="text-[10px] shrink-0">
                  {c.vendor === 'ai_generated' ? 'AI-built' : 'core'}
                </Badge>
              </div>
              <CardDescription className="text-xs font-mono">{c.id}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">{c.description}</p>
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-[10px]">
                  {c.category}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {c.status}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {c.runtime}
                </Badge>
              </div>
              {c.vendor === 'ai_generated' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-destructive"
                  onClick={() => remove(c.id)}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Remove
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {customConnectors.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No AI-built {PRODUCT_TERMS.blocks.toLowerCase()} yet. Plan an outcome in the{' '}
          {PRODUCT_TERMS.autoBlockFlowComposer} — the {PRODUCT_TERMS.autoBlocksFactory}{' '}
          {PRODUCT_TERMS.blockGenerator} will produce any new {PRODUCT_TERMS.blocks.toLowerCase()}{' '}
          needed.
        </p>
      )}
    </PageMotion>
  );
}
